export const isDanMartell = (slug = '') => (
  slug === 'coach-dan-martell-demo' || slug === 'coach-dan-martell-demo-default'
);

export const getVerticalFromHostname = (hostname) => {
  const h = String(hostname || '').toLowerCase();
  if (h.includes('pai3')) return 'pai3';
  if (h.includes('hvac')) return 'hvac';
  if (h.includes('mentormind')) return 'mentormind';
  if (h.includes('opsiq')) return 'opsiq';
  // business.opsvoice.ai maps to business vertical
  if (h.includes('business')) return 'business';
  // widget.opsvoice.ai and other unknown subdomains: rely on ?vertical= URL param
  // (handled by getUrlParams). Default to 'business' as the safest generic fallback.
  return 'business';
};

export const VERTICALS = {
  hvac: {
    title: 'OpsVoice AI Field Assistant',
    subtitle: 'Instant answers for equipment, safety & troubleshooting',
    placeholder: 'Ask about error codes, diagnostics, safety protocols…',
    quickStart: [
      'Carrier furnace error code 33 - what does it mean?',
      'What PPE for refrigerant recovery?',
      'How to check subcooling on AC unit?'
    ],
    tools: [
      { id: 'troubleshooting', name: 'Quick Diagnostics', icon: '🔧', description: 'Error codes, fault finding, system checks' },
      { id: 'safety', name: 'Safety Protocols', icon: '⚠️', description: 'Lockout/tagout, PPE, hazard prevention' },
      { id: 'equipment', name: 'Equipment Specs', icon: '📋', description: 'Manuals, wiring, part numbers' },
      { id: 'procedures', name: 'Service Procedures', icon: '🛠️', description: 'Installation, repair, maintenance steps' }
    ],
    disclaimer: 'AI field assistant. Always follow safety protocols and manufacturer guidance.',
    logo: '/opsvoice-hvac.svg',
    theme: 'hvac',
    features: {}
  },
  business: {
    title: 'Your AI Assistant',
    subtitle: 'Get instant answers from your documents',
    placeholder: 'Ask about your processes, SOPs, or company knowledge…',
    quickStart: [
      'What are our onboarding procedures?',
      'How do I submit a request?',
      'What are the steps for customer complaints?'
    ],
    tools: [
      { id: 'action-items', name: 'Action Items', icon: '✅', description: 'Extract tasks and to-dos' },
      { id: 'decision-analysis', name: 'Decision Analysis', icon: '⚖️', description: 'Analyze options and trade-offs' },
      { id: 'meeting-notes', name: 'Meeting Notes to Actions', icon: '📝', description: 'Convert notes to actionable items' },
    ],
    disclaimer: 'AI business assistant. Provides instant answers from your documents.',
    logo: '/opsvoice-business.svg',
    theme: 'business',
    features: {
      showQuickReviews: false,
      showDailyOps: false,
      showWeeklyCompliance: false,
      enableStreaming: true,
      sessionTTLDays: 7,
    }
  },
  mentormind: {
    title: 'MentorMind',
    subtitle: 'Your AI Coaching Assistant',
    placeholder: 'Ask about your goals, frameworks, or action items\u2026',
    quickStart: [
      'What should I focus on this week?',
      'Walk me through the framework',
      'What\u2019s blocking my progress?'
    ],
    tools: [
      { id: 'assessment', name: 'Assessment', icon: '📋', description: 'Clarify goals and current state' },
      { id: 'planning', name: 'Action Planning', icon: '📈', description: 'Build milestones and next steps' },
      { id: 'frameworks', name: 'Frameworks', icon: '⚡', description: 'Apply your coach\u2019s methods' }
    ],
    disclaimer: 'AI coaching assistant powered by your coach\u2019s methodology',
    logo: '/mentormind.svg',
    theme: 'mentormind',
    features: {},
    danMartell: {
      tools: [
        { id: 'roi', name: 'Time ROI Calculator', icon: '💰', description: 'Quantify time buyback' },
        { id: 'replacement-ladder', name: 'Delegation Ladder', icon: '🪜', description: 'Delegate and elevate' },
        { id: 'business-health', name: 'Business Health', icon: '📊', description: 'Score key areas' },
        { id: 'action-plans', name: 'Action Plans', icon: '🎯', description: 'Prioritize next steps' },
        { id: 'bottleneck', name: 'Constraint Finder', icon: '🔍', description: 'Find the constraint' }
      ]
    }
  },
  opsiq: {
    title: 'OpsIQ Intelligence',
    subtitle: 'Your company knowledge. Instant answers.',
    placeholder: 'Ask about your processes, SOPs, or company procedures…',
    quickStart: [
      'What are our procedures for onboarding?',
      'How do I submit an expense report?',
      'What steps for customer complaints?'
    ],
    tools: [
      { id: 'action-items', name: 'Action Items', icon: '✅', description: 'Extract tasks and to-dos' },
      { id: 'decision-analysis', name: 'Decision Analysis', icon: '⚖️', description: 'Analyze options and trade-offs' },
      { id: 'meeting-notes', name: 'Meeting Notes to Actions', icon: '📝', description: 'Convert notes to actionable items' },
    ],
    disclaimer: 'AI business assistant. Provides instant answers from your documents.',
    logo: '/opsvoice-business.svg',
    theme: 'business',
    features: {
      showQuickReviews: false,
      showDailyOps: false,
      showWeeklyCompliance: false,
      enableStreaming: true,
      sessionTTLDays: 7,
    }
  },
  pai3: {
    title: 'PAI3 Operations Assistant',
    subtitle: 'Voice + Knowledge Base powered by OpsVoice',
    placeholder: 'Ask about procedures, policies, or operations...',
    quickStart: [
      'What is our onboarding process?',
      'How do I escalate an issue?',
      'What are the safety protocols?'
    ],
    tools: [
      { id: 'procedures', name: 'Procedures', icon: '📋', description: 'Standard operating procedures' },
      { id: 'policies', name: 'Policies', icon: '📜', description: 'Company policies and guidelines' },
      { id: 'operations', name: 'Operations', icon: '⚙️', description: 'Operational workflows' }
    ],
    disclaimer: 'PAI3 Operations Assistant - Answers sourced from your documents',
    logo: null,
    logoEmoji: '⚡',
    theme: 'pai3',
    features: {}
  }
};

export const pickVerticalConfig = (vertical, slug) => {
  let v = VERTICALS[vertical] || VERTICALS.business;
  if (vertical === 'mentormind' && isDanMartell(slug)) {
    v = { ...v, tools: VERTICALS.mentormind.danMartell.tools };
  }
  return v;
};

export const getVerticalOverrides = (vertical, slug) => {
  const base = pickVerticalConfig(vertical, slug);
  return { tools: base.tools };
};


